import { Button, message, Paging, PopConfirm, Table, usePaging } from '@maxtropy/components';
import {
  apiV2MicrogridTopologyMaintenanceMonthDetailPost,
  apiV2MicrogridTopologyMaintenanceMonthSavePost,
} from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/hooks';
import EditMaintenanceMonthModal from './EditMaintenanceMonthModal';
import styles from './index.module.scss';
import { MaintenanceMonthRecord } from './types';

export interface MaintenanceMonthTabRef {
  save: () => Promise<void>;
  isEditing: boolean;
  loading: boolean;
}

const getDayFromDate = (date?: string) => {
  if (!date) {
    return undefined;
  }

  const dateValue = dayjs(date);
  return dateValue.isValid() ? dateValue.date() : undefined;
};

const buildMaintenanceDate = (month: string, day: number) => {
  const monthValue = dayjs(month);
  return monthValue.isValid() ? monthValue.date(day).format('YYYY-MM-DD') : '';
};

const MaintenanceMonthTab = forwardRef<MaintenanceMonthTabRef>((_, ref) => {
  const hasSetupPermission = useHasPermission(PermissionsType.B_MAINTENANCE_MONTH_SETUP);
  const [isEditing, setIsEditing] = useState(false);
  const [dataSource, setDataSource] = useState<MaintenanceMonthRecord[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MaintenanceMonthRecord>();
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const { refreshAsync: refreshMaintenanceMonthDetail, loading: detailLoading } = useRequest(() =>
    apiV2MicrogridTopologyMaintenanceMonthDetailPost({}).then(res => {
      const list = (res.list ?? [])
        .map((item, index) => ({
          id: item.id ?? Date.now() + index,
          month: item.maintenanceMonth ?? '',
          days: item.maintenanceDays?.map(day => getDayFromDate(day.maintenanceDate)!).sort((a, b) => a - b) ?? [],
        }))
        .sort((a, b) => b.month.localeCompare(a.month));
      setDataSource(list);
    })
  );
  const { runAsync: saveMaintenanceMonth, loading: saving } = useRequest(
    apiV2MicrogridTopologyMaintenanceMonthSavePost,
    {
      manual: true,
    }
  );

  const existingMonths = useMemo(() => dataSource.map(item => item.month), [dataSource]);
  const pagedDataSource = useMemo(
    () => dataSource.slice((pageOffset - 1) * pageSize, pageOffset * pageSize),
    [dataSource, pageOffset, pageSize]
  );

  useEffect(() => {
    setTotalCount(dataSource.length);
    const maxPage = Math.max(1, Math.ceil(dataSource.length / pageSize));
    if (pageOffset > maxPage) {
      setPageOffset(maxPage);
    }
  }, [dataSource.length, pageOffset, pageSize, setPageOffset, setTotalCount]);

  const columns: ColumnsType<MaintenanceMonthRecord> = useMemo(
    () => [
      {
        title: '检修月',
        dataIndex: 'month',
      },
      {
        title: '检修日期',
        dataIndex: 'days',
        render: days => days.join('、'),
      },
      {
        title: '操作',
        width: 180,
        render: (_, record) => (
          <Space size={12}>
            <Button type="link" htmlType="button" disabled={!isEditing} onClick={() => setEditingRecord(record)}>
              编辑
            </Button>
            <PopConfirm title="确定删除该检修月？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" htmlType="button" danger disabled={!isEditing}>
                删除
              </Button>
            </PopConfirm>
          </Space>
        ),
      },
    ],
    [isEditing]
  );

  const handleDelete = (id: number) => {
    setDataSource(list => list.filter(item => item.id !== id));
  };

  const handleModalOk = (values: MaintenanceMonthRecord) => {
    const duplicated = dataSource.some(item => item.id !== values.id && item.month === values.month);
    if (duplicated) {
      message.warning('检修月不能重复');
      return;
    }

    setDataSource(list => {
      const hasCurrent = list.some(item => item.id === values.id);
      const nextList = hasCurrent ? list.map(item => (item.id === values.id ? values : item)) : [...list, values];
      return nextList.sort((a, b) => b.month.localeCompare(a.month));
    });
    setModalOpen(false);
    setEditingRecord(undefined);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(undefined);
  };

  const handleSave = async () => {
    await saveMaintenanceMonth({
      maintenanceMonthList: dataSource.map(item => ({
        maintenanceMonth: item.month,
        maintenanceDates: item.days.map(day => buildMaintenanceDate(item.month, day)).filter(Boolean),
      })),
    });
    await refreshMaintenanceMonthDetail();
    message.success('保存成功');
    setIsEditing(false);
  };

  const handleCancel = async () => {
    await refreshMaintenanceMonthDetail();
    setIsEditing(false);
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isEditing,
    loading: detailLoading || saving,
  }));

  return (
    <>
      <Button
        type="primary"
        htmlType="button"
        wrapStyle={{ marginBottom: 10 }}
        disabled={!isEditing}
        onClick={() => setModalOpen(true)}
      >
        新增检修月
      </Button>
      <Table rowKey="id" loading={detailLoading} columns={columns} dataSource={pagedDataSource}>
        <Paging className={styles.pagination} pagingInfo={pagingInfo} showSizeChanger={false} />
      </Table>
      <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 0, marginTop: 28 }}>
        {isEditing ? (
          <>
            <Button type="primary" htmlType="button" loading={saving} onClick={handleSave}>
              保存
            </Button>
            <Button htmlType="button" onClick={handleCancel}>
              取消
            </Button>
          </>
        ) : (
          hasSetupPermission && (
            <Button type="primary" htmlType="button" onClick={() => setIsEditing(true)}>
              编辑
            </Button>
          )
        )}
      </Space>
      <EditMaintenanceMonthModal
        open={modalOpen || !!editingRecord}
        record={editingRecord}
        existingMonths={existingMonths}
        onCancel={closeModal}
        onOk={handleModalOk}
      />
    </>
  );
});

export default MaintenanceMonthTab;
